import { useContext, useEffect } from "react";
import {
  CaseFilesContext,
  CaseFilesEvent,
  CaseFilesEventCallback,
} from "./CaseFilesContext";

/**
 * Custom hook to easily subscribe to CaseFiles events
 * @param callback - Function to handle events
 * @param eventTypes - Optional array of event types to filter for (if not provided, listens to all events)
 */
export const useCaseFilesEvents = (
  callback: CaseFilesEventCallback,
  eventTypes?: CaseFilesEvent["type"][]
) => {
  const { addEventListener, removeEventListener } =
    useContext(CaseFilesContext);

  useEffect(() => {
    const wrappedCallback: CaseFilesEventCallback = (event) => {
      // If eventTypes is specified, only call callback for matching events
      if (!eventTypes || eventTypes.includes(event.type)) {
        callback(event);
      }
    };

    const cleanup = addEventListener(wrappedCallback);

    return cleanup;
  }, [addEventListener, callback, eventTypes]);
};

/**
 * Hook to listen for specific event types with individual handlers
 */
export const useCaseFilesEventHandlers = (handlers: {
  onFileClicked?: (file: any) => void;
  onFileViewed?: (file: any) => void;
  onFilesAdded?: (fileNames: string[], fileCount: number) => void;
  onFolderUploaded?: (files: any[], parent?: any) => void;
  onFilesRefreshed?: (folderId?: string) => void;
  onSearchChanged?: (search: string) => void;
  onUploadStarted?: (fileCount: number) => void;
  onUploadProgress?: (completed: number, total: number) => void;
  onUploadRejected?: (fileCount: number, message: string) => void;
}) => {
  const callback: CaseFilesEventCallback = (event) => {
    switch (event.type) {
      case "file-clicked":
        handlers.onFileClicked?.(event.payload.file);
        break;
      case "file-viewed":
        handlers.onFileViewed?.(event.payload.file);
        break;
      case "files-added":
        handlers.onFilesAdded?.(
          event.payload.fileNames,
          event.payload.fileCount
        );
        break;
      case "folder-uploaded":
        handlers.onFolderUploaded?.(event.payload.files, event.payload.parent);
        break;
      case "files-refreshed":
        handlers.onFilesRefreshed?.(event.payload.folder_id);
        break;
      case "search-changed":
        handlers.onSearchChanged?.(event.payload.search);
        break;
      case "upload-started":
        handlers.onUploadStarted?.(event.payload.fileCount);
        break;
      case "upload-progress":
        handlers.onUploadProgress?.(
          event.payload.completed,
          event.payload.total
        );
        break;
      case "upload-rejected":
        handlers.onUploadRejected?.(
          event.payload.fileCount,
          event.payload.message
        );
        break;
    }
  };

  useCaseFilesEvents(callback);
};
